import { CodeConfigurationField } from '@dtx-company/inter-app/src/types/flowcode'
import { FC, useEffect, useMemo, useRef } from 'react'
import { FLAGS_BY_REGION, RegionCode } from '@dtx-company/inter-app/src/constants/flagsByRegion'
import { Grid, Select, Stack, TextArea, TextField } from '@dtx-company/design-system/src'
import { MESSAGE_MAX_LENGTH } from '../constants'
import { PhoneOutlined } from '@mui/icons-material'
import { ScanDestinationFieldProps } from '../ScanDestination'
import { fireAnalyticsEvent } from '@dtx-company/inter-app/src/event-tracking/helpers/fireAnalyticsEvent'
import { getCountryCodeForRegionCode, getSupportedRegionCodes } from 'awesome-phonenumber'
import {
  selectBody,
  selectError,
  selectNormalizedScanDestination,
  selectRegionCode
} from '@dtx-company/app/shared/code/src/machines/locg/locgSelectors'
import { useGlobalServices } from '@dtx-company/app/shared/common/src/hooks/useGlobalServices'
import { useOnChange } from '../hooks/useOnChange'
import { useScreenSizeBreakpoints } from '@dtx-company/app/shared/common/src/hooks/useScreenSizeBreakpoints'
import { useSelector } from '@xstate/react'
import useTranslation from 'next-translate/useTranslation'

export const SMSField: FC<ScanDestinationFieldProps> = ({ size = 'large' }) => {
  const { t } = useTranslation('logged-out-generator')
  const { locgService } = useGlobalServices()
  const scanDestination = useSelector(locgService, selectNormalizedScanDestination)
  const regionCode = useSelector(locgService, selectRegionCode)
  const body = useSelector(locgService, selectBody)
  const recipientError = useSelector(
    locgService,
    selectError
  )(CodeConfigurationField.SCAN_DESTINATION)
  const messageError = useSelector(locgService, selectError)(CodeConfigurationField.BODY)

  const recipientRef = useRef<HTMLInputElement>(null)
  const messageRef = useRef<HTMLInputElement>(null)

  const { isMobile } = useScreenSizeBreakpoints()

  useEffect(() => {
    if (recipientError) {
      recipientRef.current?.scrollIntoView({
        block: isMobile ? 'start' : 'center',
        behavior: 'smooth'
      })
    } else if (messageError) {
      messageRef.current?.scrollIntoView({
        block: isMobile ? 'start' : 'center',
        behavior: 'smooth'
      })
    }
  }, [isMobile, messageError, recipientError])

  const regionSelectOptions = useMemo(() => {
    return (getSupportedRegionCodes() as RegionCode[]).map(regionCode => ({
      label: `${regionCode} ${FLAGS_BY_REGION[regionCode]} (+${getCountryCodeForRegionCode(regionCode)})`,
      value: regionCode
    }))
  }, [])

  const onChange = useOnChange()

  return (
    <Stack gap={{ xs: 2, md: 4 }}>
      <Grid
        container
        spacing={2}
        sx={{
          '.MuiFormControl-root': {
            minWidth: 'unset'
          }
        }}
      >
        <Grid size={{ xs: 5, md: 4 }}>
          <Select
            size={size}
            name={CodeConfigurationField.REGION_CODE}
            label={t(
              'LoggedOutGenerator.ScanDestination.options.sms.subcomponents.RegionSelector.label'
            )}
            hiddenLabel
            value={regionCode}
            options={regionSelectOptions}
            onChange={onChange}
            onFocus={() =>
              fireAnalyticsEvent('LoggedOutGenerator_ScanDestinationInputSMSRegion_Focused')
            }
            data-heap-id="LoggedOutGenerator_ScanDestinationInputSMSRegionSelector"
          />
        </Grid>
        <Grid size={{ xs: 7, md: 8 }}>
          <TextField
            size={size}
            name={CodeConfigurationField.SCAN_DESTINATION}
            label={t(
              'LoggedOutGenerator.ScanDestination.options.sms.subcomponents.PhoneNumberInput.label'
            )}
            hiddenLabel
            value={scanDestination}
            placeholder={t(
              'LoggedOutGenerator.ScanDestination.options.sms.subcomponents.PhoneNumberInput.placeholder'
            )}
            type="tel"
            startAdornment={<PhoneOutlined sx={{ color: 'onSurface.secondary' }} />}
            onChange={onChange}
            onFocus={() =>
              fireAnalyticsEvent('LoggedOutGenerator_ScanDestinationInputSMSRecipient_Focused')
            }
            onBlur={e => {
              fireAnalyticsEvent('LoggedOutGenerator_ScanDestinationInputSMSRecipient_Blurred', {
                text: e.target.value
              })
            }}
            inputProps={{
              'data-heap-id': 'LoggedOutGenerator_ScanDestinationInputSMSRecipient',
              ref: recipientRef
            }}
            fullWidth
            error={!!recipientError}
            helperText={recipientError}
          />
        </Grid>
      </Grid>

      <TextArea
        name={CodeConfigurationField.BODY}
        label={t('LoggedOutGenerator.ScanDestination.options.sms.subcomponents.MessageInput.label')}
        hiddenLabel
        value={body}
        placeholder={t(
          'LoggedOutGenerator.ScanDestination.options.sms.subcomponents.MessageInput.placeholder'
        )}
        onChange={onChange}
        onFocus={() =>
          fireAnalyticsEvent('LoggedOutGenerator_ScanDestinationInputSMSMessage_Focused')
        }
        onBlur={e => {
          locgService.send({ type: 'NEXT' })
          fireAnalyticsEvent('LoggedOutGenerator_ScanDestinationInputSMSMessage_Blurred', {
            text: e.target.value
          })
        }}
        helperText={
          messageError ||
          t(
            'LoggedOutGenerator.ScanDestination.options.sms.subcomponents.MessageInput.helperText',
            {
              characterCount: body?.length ?? 0,
              characterLimit: MESSAGE_MAX_LENGTH
            }
          )
        }
        inputProps={{
          'data-heap-id': 'LoggedOutGenerator_ScanDestinationInputSMSMessage',
          ref: messageRef
        }}
        error={!!messageError}
      />
    </Stack>
  )
}
